"use client";

import React, { createContext, useContext, useMemo } from "react";
import * as Sentry from "@sentry/nextjs";

import { useEnvironmentVariables } from "../environmentVariables";
import useSessionRemovedListener from "../session/useSessionRemovedListener";

const LOGGING_LEVEL = {
	FATAL_EXCEPTION: "FATAL_EXCEPTION",
	EXCEPTION: "EXCEPTION",
	MESSAGE: "MESSAGE",
	UNKNOWN: "UNKNOWN",
};

const loggingMethod = {
	// eslint-disable-next-line no-console -- the console is this logger's dev output
	[LOGGING_LEVEL.FATAL_EXCEPTION]: console.error,
	// eslint-disable-next-line no-console -- the console is this logger's dev output
	[LOGGING_LEVEL.EXCEPTION]: console.error,
	// eslint-disable-next-line no-console -- the console is this logger's dev output
	[LOGGING_LEVEL.MESSAGE]: console.log,
	// eslint-disable-next-line no-console -- the console is this logger's dev output
	[LOGGING_LEVEL.UNKNOWN]: console.log,
};

const sentryCaptureUsing = (method, defaultOptions = {}) => {
	const capture = Sentry[method];

	return (event, options = {}) => {
		const { tags, level, loggingLevel, runtimeEnvironment } = {
			...defaultOptions,
			...options,
		};

		Sentry.withScope((scope) => {
			if (level) {
				scope.setLevel(level);
			}

			if (tags) {
				Object.entries(tags).forEach(([key, value]) => {
					scope.setTag(key, value);
				});
			}

			capture(event);
		});

		if (runtimeEnvironment === "caruso-dev") {
			const log = loggingMethod[loggingLevel ?? LOGGING_LEVEL.UNKNOWN];
			log(`[${loggingLevel ?? LOGGING_LEVEL.UNKNOWN}]`, event);
		}
	};
};

// Client-side Sentry.init now lives in `instrumentation-client.ts` (the
// `@sentry/nextjs` convention), so this provider only consumes the already
// initialised global client.
const LoggerContext = createContext();

export function useLogger() {
	const context = useContext(LoggerContext);

	if (!context) {
		throw new Error("useLogger should always be used within a LoggerProvider");
	}

	return context;
}

export const LoggerProvider = ({ children, ...rest }) => {
	useSessionRemovedListener(() => {
		Sentry.setUser({});
		// Clear the active investing-entity tag on logout. Logout removes the
		// session client-side without a page reload, so without this a later
		// session in the same tab would inherit the previous investor's entity id
		// until the next setLastActiveInvestingEntity call (APP-23988).
		Sentry.setTag("investing-entity-id", undefined);
	});

	const { runtimeEnvironment } = useEnvironmentVariables();

	const value = useMemo(
		() => ({
			setUser: Sentry.setUser,
			// Sometimes we want to capture, but not send to Sentry
			captureNothing() {},
			captureMessage: sentryCaptureUsing("captureMessage", {
				loggingLevel: LOGGING_LEVEL.MESSAGE,
				runtimeEnvironment,
			}),
			captureException: sentryCaptureUsing("captureException", {
				loggingLevel: LOGGING_LEVEL.EXCEPTION,
				runtimeEnvironment,
			}),
			captureFatalException: sentryCaptureUsing("captureException", {
				loggingLevel: LOGGING_LEVEL.FATAL_EXCEPTION,
				runtimeEnvironment,
				level: "fatal",
			}),
		}),
		[runtimeEnvironment],
	);

	return (
		<LoggerContext.Provider value={value} {...rest}>
			{children}
		</LoggerContext.Provider>
	);
};
